import z from "zod";

import { type ErrorDetails, RemiHttpCode } from "./remi-app";

const westhillIntegrationErrorTypeValues = [
  "WESTHILL_ASSIGNMENT_NOT_FOUND",
  "ASSIGNMENT_IS_MISSING_EXTERNAL_ID",
  "FILE_NOT_FOUND",
  "FILE_UPLOAD_FAILED",
  "REMI_MESSAGE_ALREADY_POSTED",
  "NOTE_POST_FAILED",
  "NOTE_CONTENT_MISMATCH",
  "NOTE_NUMBERS_NOT_NORMALIZED",
  "LOGIN_COOLDOWN",
  "LOGIN_FAILED",
  "LOGIN_SCRIPT_FAILED",
  "LOGIN_ALREADY_IN_PROGRESS",
  "XACTANALYSIS_CYCLE_FAILED",
  "UNKNOWN_LOGIN_PORTAL",
  "ASSIGNMENT_COUNT_MISMATCH",
  "USER_COUNT_MISMATCH",
  "USER_NOT_FOUND",
  "UNHANDLED_REF_SOURCE",
  "INVALID_INGEST_MESSAGE",
  "UNKNOWN_EVENT_TYPE",
  "INVALID_EVENT_DATA",
  "EVENT_ALL_RETRIES_FAILED",
  "WEBHOOK_HEADER_MISSING",
  "WEBHOOK_SIGNATURE_VERIFICATION_FAILED",
  "UNKNOWN_ENUM_VALUE",
  "EVENT_TYPE_MISMATCH",
  "WESTHILL_UNIFIED_EVENTS_NOT_RAN_FIRE_ALARM",
  "WESTHILL_UNIFIED_EVENTS_FAILED_FIRE_ALARM",
  "WESTHILL_UNIFIED_UNSYNCED_NOTES_FIRE_ALARM",
  "WESTHILL_UNIFIED_UNSYNCED_ASSIGNMENT_FIRE_ALARM",
  "WESTHILL_XA_EVENTS_NOT_RAN_FIRE_ALARM",
  "WESTHILL_XA_EVENTS_FAILED_FIRE_ALARM",
  "WESTHILL_XA_UNSYNCED_NOTES_FIRE_ALARM",
  "WESTHILL_XA_UNSYNCED_ASSIGNMENT_FIRE_ALARM",
  "WESTHILL_XA_UNSYNCED_CONTACTS_FIRE_ALARM",
  "WESTHILL_XA_STALE_ASSIGNMENT_SYNC_FIRE_ALARM",
  "WESTHILL_XA_SUSTAINED_SYNC_FAILURE",
  "XA_SESSION_EXPIRED",
  "FAILED_TO_FIND_IFRAME",
  "FAILED_TO_PARSE_TIME",
  "FAILED_TO_POST_STATUS_UPDATE",
  "COULD_NOT_FIND_OPTION_VALUE",
  "WESTHILL_UNIFIED_ASSIGNMENT_SYNC_STALLED",
  "WESTHILL_UNIFIED_NOTES_VIBE_CHECK_FAILED",
  "WESTHILL_XA_ASSIGNMENTS_VIBE_CHECK_FAILED",
  "WESTHILL_XA_NOTES_VIBE_CHECK_FAILED",
  "STATUS_UPDATE_BUTTON_NOT_FOUND",
  "STATUS_UPDATE_IFRAME_NOT_FOUND",
  "UNKNOWN_DATASET_ID",
  "XA_SCRAPE_RETURNED_NO_ASSIGNMENTS",
  "DENVER_TIME_PARSE_FAILED",
  "UPLOAD_WIDGET_NOT_READY",
  "XA_FILE_UPLOAD_FAILED",
  "FILE_TOO_LARGE",
  "UNSUPPORTED_FILE_TYPE",
] as const;
const westhillIntegrationErrorTypeSchema = z.enum(westhillIntegrationErrorTypeValues);
const WesthillIntegrationErrorType = westhillIntegrationErrorTypeSchema.enum;
type WesthillIntegrationErrorType = z.infer<typeof westhillIntegrationErrorTypeSchema>;

export type WesthillIntegrationErrors =
  | {
      errorType: typeof WesthillIntegrationErrorType.WESTHILL_ASSIGNMENT_NOT_FOUND;
      data: { assignmentId: string };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.ASSIGNMENT_IS_MISSING_EXTERNAL_ID;
      data: { assignmentId: string };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.FILE_NOT_FOUND;
      data: { assignmentId: string } & ({ fileId: string; fileName?: never } | { fileName: string; fileId?: never });
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.FILE_UPLOAD_FAILED;
      data: { assignmentId: string; remiFileId: string };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.REMI_MESSAGE_ALREADY_POSTED;
      data: { messageId: string; existingNoteId: string };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.NOTE_POST_FAILED;
      data: { assignmentId: string; messageId?: string };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.NOTE_CONTENT_MISMATCH;
      data: {
        assignmentId: string;
        postedNoteId: string;
        postedLength: number;
        expectedLength: number;
      };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.NOTE_NUMBERS_NOT_NORMALIZED;
      data: { assignmentId: string; mfn: string; minNormalNoteNumber: number };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.LOGIN_COOLDOWN;
      data: { cooldownMinutes: number };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.LOGIN_FAILED;
      data: { message: string | undefined };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.LOGIN_SCRIPT_FAILED;
      data: { message: string | undefined };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.LOGIN_ALREADY_IN_PROGRESS;
      data: { scriptName: string };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.XACTANALYSIS_CYCLE_FAILED;
      data: { errorName: string };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.UNKNOWN_LOGIN_PORTAL;
      data: { url: string };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.ASSIGNMENT_COUNT_MISMATCH;
      data: { expectedCount: number; actualCount: number };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.USER_COUNT_MISMATCH;
      data: { expectedCount: number; actualCount: number };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.USER_NOT_FOUND;
      data: { userId: string };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.UNHANDLED_REF_SOURCE;
      data: { refSource: string };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.INVALID_INGEST_MESSAGE;
      data: { issues: Array<{ path: string; code: string }> };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.UNKNOWN_EVENT_TYPE;
      data: { eventId: string; eventType: string };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.INVALID_EVENT_DATA;
      data: { eventId: string; error: unknown };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.EVENT_ALL_RETRIES_FAILED;
      data: { eventId: string; eventType: string; error: unknown };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.WEBHOOK_HEADER_MISSING;
      data: { header: string };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.WEBHOOK_SIGNATURE_VERIFICATION_FAILED;
      data: undefined;
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.UNKNOWN_ENUM_VALUE;
      data: { column: string; value: unknown };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.EVENT_TYPE_MISMATCH;
      data: { eventType: string; parsedEventType: unknown };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.WESTHILL_UNIFIED_EVENTS_NOT_RAN_FIRE_ALARM;
      data: { count: number };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.WESTHILL_UNIFIED_EVENTS_FAILED_FIRE_ALARM;
      data: { count: number };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.WESTHILL_UNIFIED_UNSYNCED_NOTES_FIRE_ALARM;
      data: { count: number };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.WESTHILL_UNIFIED_UNSYNCED_ASSIGNMENT_FIRE_ALARM;
      data: { count: number };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.WESTHILL_XA_EVENTS_NOT_RAN_FIRE_ALARM;
      data: { count: number };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.WESTHILL_XA_EVENTS_FAILED_FIRE_ALARM;
      data: { count: number };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.WESTHILL_XA_UNSYNCED_NOTES_FIRE_ALARM;
      data: { count: number };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.WESTHILL_XA_UNSYNCED_ASSIGNMENT_FIRE_ALARM;
      data: { count: number };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.WESTHILL_XA_UNSYNCED_CONTACTS_FIRE_ALARM;
      data: { count: number };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.WESTHILL_XA_STALE_ASSIGNMENT_SYNC_FIRE_ALARM;
      data: {
        hotStaleCount: number;
        warmStaleCount: number;
        coldStaleCount: number;
        sampleAssignmentIds: string;
        oldestLastSyncedAt: string | null;
      };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.WESTHILL_XA_SUSTAINED_SYNC_FAILURE;
      data: {
        badCycleCount: number;
        windowMinutes: number;
        processedCount: number;
        erroredCount: number;
        cycleQueuedCount: number;
        breakerTripped: boolean;
        bootstrapFailed: boolean;
      };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.XA_SESSION_EXPIRED;
      data: { url: string };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.FAILED_TO_FIND_IFRAME;
      data: { assignmentId: string; eventType: string };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.FAILED_TO_PARSE_TIME;
      data: { eventId: string; date: string };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.FAILED_TO_POST_STATUS_UPDATE;
      data: { eventId: string; eventType: string };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.COULD_NOT_FIND_OPTION_VALUE;
      data: { optionText: string; programType: string; datasetId: string };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.WESTHILL_UNIFIED_ASSIGNMENT_SYNC_STALLED;
      data: { lastSyncedAt: Date | null; stalenessMinutes: number };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.WESTHILL_UNIFIED_NOTES_VIBE_CHECK_FAILED;
      data: { lastRecordAt: Date | null; thresholdMinutes: number };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.WESTHILL_XA_ASSIGNMENTS_VIBE_CHECK_FAILED;
      data: { lastRecordAt: Date | null; thresholdMinutes: number };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.WESTHILL_XA_NOTES_VIBE_CHECK_FAILED;
      data: { lastRecordAt: Date | null; thresholdMinutes: number };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.STATUS_UPDATE_BUTTON_NOT_FOUND;
      data: { assignmentId: string; eventType: string; statusName: string };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.STATUS_UPDATE_IFRAME_NOT_FOUND;
      data: { assignmentId: string; eventType: string; statusName: string };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.UNKNOWN_DATASET_ID;
      data: { datasetId: string };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.XA_SCRAPE_RETURNED_NO_ASSIGNMENTS;
      data: { datasetIds: string[] };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.DENVER_TIME_PARSE_FAILED;
      data: { parts: string };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.UPLOAD_WIDGET_NOT_READY;
      data: { assignmentId: string; mfn: string; fileId: string };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.XA_FILE_UPLOAD_FAILED;
      data: { assignmentId: string; mfn: string; fileId: string; uploadResponseStatus: number };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.FILE_TOO_LARGE;
      data: { assignmentId: string; mfn: string; fileId: string };
    }
  | {
      errorType: typeof WesthillIntegrationErrorType.UNSUPPORTED_FILE_TYPE;
      data: { assignmentId: string; mfn: string; fileId: string };
    };

export const westhillIntegrationErrorDetails: Record<WesthillIntegrationErrorType, ErrorDetails> = {
  [WesthillIntegrationErrorType.WESTHILL_ASSIGNMENT_NOT_FOUND]: {
    sentryDescription: "Westhill assignment not found",
    remiHttpCode: RemiHttpCode.NOT_FOUND,
  },
  [WesthillIntegrationErrorType.ASSIGNMENT_IS_MISSING_EXTERNAL_ID]: {
    sentryDescription: "Westhill assignment is missing external ID",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [WesthillIntegrationErrorType.FILE_NOT_FOUND]: {
    sentryDescription: "File not found in Westhill assignment files list",
    remiHttpCode: RemiHttpCode.NOT_FOUND,
  },
  [WesthillIntegrationErrorType.FILE_UPLOAD_FAILED]: {
    sentryDescription: "Failed to upload file to Westhill Unified",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [WesthillIntegrationErrorType.REMI_MESSAGE_ALREADY_POSTED]: {
    sentryDescription: "Remi message has already been posted to Westhill Unified - skipping duplicate",
    remiHttpCode: RemiHttpCode.BAD_REQUEST,
  },
  [WesthillIntegrationErrorType.NOTE_POST_FAILED]: {
    sentryDescription:
      "Portal API returned empty or failed response after posting message - note may not have been created",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [WesthillIntegrationErrorType.NOTE_CONTENT_MISMATCH]: {
    sentryDescription: "Posted note content does not match the message that was sent - possible data corruption",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [WesthillIntegrationErrorType.NOTE_NUMBERS_NOT_NORMALIZED]: {
    sentryDescription:
      "XactAnalysis note numbers passed to upsert did not start at 1 - possible applyNoteNumberOffset regression",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [WesthillIntegrationErrorType.LOGIN_COOLDOWN]: {
    sentryDescription: "Westhill login blocked - circuit breaker active after previous authentication failure",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [WesthillIntegrationErrorType.LOGIN_FAILED]: {
    sentryDescription: "Westhill Puppeteer login script returned no session cookies",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [WesthillIntegrationErrorType.LOGIN_SCRIPT_FAILED]: {
    sentryDescription: "Westhill Puppeteer login script threw before returning cookies",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [WesthillIntegrationErrorType.LOGIN_ALREADY_IN_PROGRESS]: {
    sentryDescription: "Westhill Puppeteer login skipped because another login run was already in progress",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [WesthillIntegrationErrorType.XACTANALYSIS_CYCLE_FAILED]: {
    sentryDescription: "XactAnalysis automation cycle threw out of its Puppeteer script",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [WesthillIntegrationErrorType.UNKNOWN_LOGIN_PORTAL]: {
    sentryDescription: "XactAnalysis login redirected to an unrecognized portal — Verisk may have changed login flows",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [WesthillIntegrationErrorType.ASSIGNMENT_COUNT_MISMATCH]: {
    sentryDescription: "Westhill API returned fewer assignments than reported count - possible pagination issue",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [WesthillIntegrationErrorType.USER_COUNT_MISMATCH]: {
    sentryDescription: "Westhill API returned fewer Remi users than reported count - possible pagination issue",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [WesthillIntegrationErrorType.USER_NOT_FOUND]: {
    sentryDescription: "User ID not found in Remi or Westhill user lists during note processing",
    remiHttpCode: RemiHttpCode.NOT_FOUND,
  },
  [WesthillIntegrationErrorType.UNHANDLED_REF_SOURCE]: {
    sentryDescription: "Unhandled refSource value in message ingestion",
    remiHttpCode: RemiHttpCode.BAD_REQUEST,
  },
  [WesthillIntegrationErrorType.INVALID_INGEST_MESSAGE]: {
    sentryDescription: "Inbound ingest-message payload failed schema validation (e.g. unrecognized message.refSource)",
    remiHttpCode: RemiHttpCode.BAD_REQUEST,
  },
  [WesthillIntegrationErrorType.UNKNOWN_EVENT_TYPE]: {
    sentryDescription: "Event processor encountered unknown event type",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [WesthillIntegrationErrorType.INVALID_EVENT_DATA]: {
    sentryDescription: "Event data failed Zod validation",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [WesthillIntegrationErrorType.EVENT_ALL_RETRIES_FAILED]: {
    sentryDescription: "Event processing exhausted all retry attempts",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [WesthillIntegrationErrorType.WEBHOOK_HEADER_MISSING]: {
    sentryDescription: "Required Svix webhook header is missing",
    remiHttpCode: RemiHttpCode.BAD_REQUEST,
  },
  [WesthillIntegrationErrorType.WEBHOOK_SIGNATURE_VERIFICATION_FAILED]: {
    sentryDescription: "Svix webhook signature verification failed",
    remiHttpCode: RemiHttpCode.UNAUTHORIZED,
  },
  [WesthillIntegrationErrorType.UNKNOWN_ENUM_VALUE]: {
    sentryDescription: "Encountered unknown enum value from Westhill API",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [WesthillIntegrationErrorType.EVENT_TYPE_MISMATCH]: {
    sentryDescription: "Mismatch between eventType and parsedEventType",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [WesthillIntegrationErrorType.WESTHILL_UNIFIED_EVENTS_NOT_RAN_FIRE_ALARM]: {
    sentryDescription: "Westhill Unified has pending events that have not run within the expected window",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [WesthillIntegrationErrorType.WESTHILL_UNIFIED_EVENTS_FAILED_FIRE_ALARM]: {
    sentryDescription: "Westhill Unified has events that failed after all retry attempts",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [WesthillIntegrationErrorType.WESTHILL_UNIFIED_UNSYNCED_NOTES_FIRE_ALARM]: {
    sentryDescription: "Westhill Unified has notes older than threshold without external IDs",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [WesthillIntegrationErrorType.WESTHILL_UNIFIED_UNSYNCED_ASSIGNMENT_FIRE_ALARM]: {
    sentryDescription: "Westhill Unified has assignments older than threshold without external IDs",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [WesthillIntegrationErrorType.WESTHILL_XA_EVENTS_NOT_RAN_FIRE_ALARM]: {
    sentryDescription: "Westhill XactAnalysis has pending events that have not run within the expected window",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [WesthillIntegrationErrorType.WESTHILL_XA_EVENTS_FAILED_FIRE_ALARM]: {
    sentryDescription: "Westhill XactAnalysis has events that failed after all retry attempts",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [WesthillIntegrationErrorType.WESTHILL_XA_UNSYNCED_NOTES_FIRE_ALARM]: {
    sentryDescription: "Westhill XactAnalysis has notes older than threshold without external IDs",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [WesthillIntegrationErrorType.WESTHILL_XA_UNSYNCED_ASSIGNMENT_FIRE_ALARM]: {
    sentryDescription: "Westhill XactAnalysis has assignments older than threshold without external IDs",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [WesthillIntegrationErrorType.WESTHILL_XA_UNSYNCED_CONTACTS_FIRE_ALARM]: {
    sentryDescription: "Westhill XactAnalysis has assignment contacts older than threshold not yet synced to Remi",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [WesthillIntegrationErrorType.WESTHILL_XA_STALE_ASSIGNMENT_SYNC_FIRE_ALARM]: {
    sentryDescription: "Westhill XactAnalysis has assignments that have not been re-synced within their tier's cadence",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [WesthillIntegrationErrorType.WESTHILL_XA_SUSTAINED_SYNC_FAILURE]: {
    sentryDescription: "Westhill XactAnalysis sync cycles have been failing repeatedly within the last hour",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [WesthillIntegrationErrorType.XA_SESSION_EXPIRED]: {
    sentryDescription: "XactAnalysis Verisk session expired — page redirected to Verisk login or session is invalid",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [WesthillIntegrationErrorType.FAILED_TO_FIND_IFRAME]: {
    sentryDescription: "Failed to find iframe for Westhill XactAnalysis status update form",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [WesthillIntegrationErrorType.FAILED_TO_PARSE_TIME]: {
    sentryDescription: "Failed to parse time from date",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [WesthillIntegrationErrorType.FAILED_TO_POST_STATUS_UPDATE]: {
    sentryDescription: "Failed to post status update for event in Westhill XactAnalysis",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [WesthillIntegrationErrorType.COULD_NOT_FIND_OPTION_VALUE]: {
    sentryDescription: "Could not find option value in XactAnalysis dropdown",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [WesthillIntegrationErrorType.WESTHILL_UNIFIED_ASSIGNMENT_SYNC_STALLED]: {
    sentryDescription: "Westhill Unified assignment sync has not written within the staleness window",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [WesthillIntegrationErrorType.WESTHILL_UNIFIED_NOTES_VIBE_CHECK_FAILED]: {
    sentryDescription: "No new Westhill Unified notes within lookback window",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [WesthillIntegrationErrorType.WESTHILL_XA_ASSIGNMENTS_VIBE_CHECK_FAILED]: {
    sentryDescription: "No new XactAnalysis assignments within lookback window",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [WesthillIntegrationErrorType.WESTHILL_XA_NOTES_VIBE_CHECK_FAILED]: {
    sentryDescription: "No new XactAnalysis notes within lookback window",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [WesthillIntegrationErrorType.STATUS_UPDATE_BUTTON_NOT_FOUND]: {
    sentryDescription: "Status update button not found on XactAnalysis workflow status page",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [WesthillIntegrationErrorType.STATUS_UPDATE_IFRAME_NOT_FOUND]: {
    sentryDescription: "XactAnalysis status update iframe did not load after clicking edit button",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [WesthillIntegrationErrorType.UNKNOWN_DATASET_ID]: {
    sentryDescription: "XactAnalysis assignment has a dataset ID with no configured program type",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [WesthillIntegrationErrorType.XA_SCRAPE_RETURNED_NO_ASSIGNMENTS]: {
    sentryDescription: "XactAnalysis assignment scrape returned no assignments across every configured dataset",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [WesthillIntegrationErrorType.DENVER_TIME_PARSE_FAILED]: {
    sentryDescription: "Intl.DateTimeFormat failed to produce a Denver hour/minute for the XA sync cold-window check",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [WesthillIntegrationErrorType.UPLOAD_WIDGET_NOT_READY]: {
    sentryDescription:
      "XactAnalysis document upload button never enabled or rendered an inline error after file selection",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [WesthillIntegrationErrorType.XA_FILE_UPLOAD_FAILED]: {
    sentryDescription: "XactAnalysis returned a non-200 status for the document upload POST",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [WesthillIntegrationErrorType.FILE_TOO_LARGE]: {
    sentryDescription: "Remi file exceeds the XactAnalysis 15MB upload cap",
    remiHttpCode: RemiHttpCode.BAD_REQUEST,
  },
  [WesthillIntegrationErrorType.UNSUPPORTED_FILE_TYPE]: {
    sentryDescription: "Remi file bytes are not a supported document type for XactAnalysis upload",
    remiHttpCode: RemiHttpCode.BAD_REQUEST,
  },
};
